import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    debitPackageCostService,
    debitProductCostService,
} from '../../../services/company.service';
import LoaderSpinner from '../../common/Loader';
import InputCalculator from '../../common/InputCalculator';

/* istanbul ignore next */
const PostChargeOnTaskModal = ({ show, handleCancel, task, productContext, onPosted }) => {
    const [amount, setAmount] = useState('');
    const [comment, setComment] = useState('');
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(false);

    useEffect(() => {
        if (show) {
            setAmount('');
            setComment('');
            setErrors({});
        }
    }, [show, task?.taskId]);

    const validate = () => {
        const e = {};
        const amt = Number(amount);
        if (!amount || Number.isNaN(amt) || amt <= 0) e.amount = 'Enter a valid amount';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const taskId = task?.taskId ?? task?.id;
        if (!taskId) {
            toast.error('Missing task reference.');
            return;
        }
        setLoader(true);
        try {
            const referenceType = productContext?.referenceType;
            const basePayload = {
                taskId,
                debitAmount: Number(amount),
                debitComment: comment || '',
                transactionDate: new Date().toISOString(),
                isStandAlone: false,
            };

            let response;
            if (referenceType === 'Package') {
                response = await debitPackageCostService({
                    ...basePayload,
                    packageId: productContext?.referenceId || 0,
                    packageTrackingNumber:
                        productContext?.packageTrackingNumber || productContext?.sku || '',
                });
            } else {
                response = await debitProductCostService({
                    ...basePayload,
                    productId:
                        productContext?.referenceId || productContext?.inboundProductId || 0,
                    returnProductSku:
                        productContext?.returnProductSku || productContext?.sku || '',
                });
            }
            if (response?.data?.Status === 200) {
                toast.success('Charge posted successfully');
                if (onPosted) onPosted(response.data.Entity);
                handleCancel();
            } else {
                toast.error(
                    response?.data?.Message?.AppStatusDescription || 'Failed to post charge'
                );
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred while posting the charge.');
        } finally {
            setLoader(false);
        }
    };

    const renderHeader = () => {
        const parts = [];
        if (task?.taskId) parts.push(`#${task.taskId}`);
        if (task?.taskTitle) parts.push(task.taskTitle);
        if (task?.taskType) parts.push(task.taskType);
        return parts.join('  \u00B7  ');
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static" size="lg">
            <Modal.Header>
                <div>
                    <Modal.Title>Post Charge on Task</Modal.Title>
                    {renderHeader() && (
                        <div className="text-muted small mt-1">{renderHeader()}</div>
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    className="btn btn-link p-0 border-0"
                    style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, color: '#6c757d' }}
                    onClick={handleCancel}
                >
                    <i className="fa fa-times" />
                </button>
            </Modal.Header>
            <Modal.Body>
                {loader && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}
                <Row>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Enter Amount ($) <span className="require-field">*</span>
                        </div>
                        <InputCalculator
                            value={String(amount ?? '')}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="Enter Amount"
                            maxLength={20}
                        />
                        {errors.amount && (
                            <span className="error-message">{errors.amount}</span>
                        )}
                    </Col>
                    <Col xs={12} className="mb-3">
                        <div className="input-label-txt">Comment</div>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Enter comment..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn cus-primary-bg-btn"
                    disabled={loader}
                    onClick={handleSubmit}
                >
                    Charge
                </button>
                <button
                    type="button"
                    className="btn cus-primary-transparent-btn"
                    disabled={loader}
                    onClick={handleCancel}
                >
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
};

PostChargeOnTaskModal.propTypes = {
    show: PropTypes.bool,
    handleCancel: PropTypes.func,
    task: PropTypes.object,
    productContext: PropTypes.object,
    onPosted: PropTypes.func,
};

export default PostChargeOnTaskModal;
